import React, { useCallback, useEffect, useRef, useState } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, CloudArrowUpIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import {
  AdminApi,
  AdminCourseContent,
  AdminTestObject,
  AdminTestPaper,
  AdminVideo,
  AdminVideoUploadProgress,
  ADMIN_VIDEO_MAX_BYTES,
} from '../../services/adminApi';
import { toast } from 'react-toastify';
import AdminRichTextEditor from './AdminRichTextEditor';

interface CourseContentEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  courseId: number;
  initialData?: AdminCourseContent;
  contentType: 'VIDEO' | 'POST' | 'FORDER' | 'TEST';
  parentId?: number | null;
}

const dataUrlToFile = async (dataUrl: string, filename: string): Promise<File> => {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], filename, { type: blob.type || 'image/png' });
};

const replaceBase64ImagesInHtml = async (html: string): Promise<string> => {
  if (!html || !html.includes('data:image')) return html;
  const regex = /src=(["'])(data:image\/[a-zA-Z0-9+.-]+;base64,[^"']+)\1/gi;
  const matches = [...html.matchAll(regex)];
  if (!matches.length) return html;

  let next = html;
  for (let i = 0; i < matches.length; i += 1) {
    const dataUrl = matches[i][2];
    try {
      const file = await dataUrlToFile(dataUrl, `paste-${Date.now()}-${i}.png`);
      const res = await AdminApi.uploadImage(file);
      if (res.success && res.data?.url) {
        next = next.split(dataUrl).join(res.data.url);
      }
    } catch (err) {
      console.error('Failed to upload pasted image', err);
    }
  }
  return next;
};

const typeLabel = (type: string) => {
  switch (type) {
    case 'FORDER':
      return 'Thư mục';
    case 'POST':
      return 'Bài viết';
    case 'VIDEO':
      return 'Video';
    case 'TEST':
      return 'Quiz';
    default:
      return type;
  }
};

const CourseContentEditorModal: React.FC<CourseContentEditorModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  courseId,
  initialData,
  contentType,
  parentId,
}) => {
  const [name, setName] = useState('');
  const [titleManuallyEdited, setTitleManuallyEdited] = useState(false);
  const [detail, setDetail] = useState('');
  const [objectId, setObjectId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState<AdminVideoUploadProgress | null>(null);

  // Video picker
  const [videoQuery, setVideoQuery] = useState('');
  const [videos, setVideos] = useState<AdminVideo[]>([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<AdminVideo | null>(null);

  // Test picker
  const [testQuery, setTestQuery] = useState('');
  const [testObjectFilter, setTestObjectFilter] = useState<number | ''>('');
  const [testObjects, setTestObjects] = useState<AdminTestObject[]>([]);
  const [papers, setPapers] = useState<AdminTestPaper[]>([]);
  const [loadingPapers, setLoadingPapers] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState<AdminTestPaper | null>(null);

  const base64UploadTimerRef = useRef<number | null>(null);

  /** Nếu user chưa sửa tiêu đề thủ công → luôn sync theo video/quiz đang chọn */
  const applySourceTitle = useCallback(
    (sourceTitle: string) => {
      if (titleManuallyEdited) return;
      const next = (sourceTitle || '').trim();
      if (next) setName(next);
    },
    [titleManuallyEdited]
  );

  useEffect(() => {
    if (!isOpen) return;
    setName(initialData?.name || '');
    setTitleManuallyEdited(false);
    setDetail(initialData?.detail || '');
    setObjectId(initialData?.objectId ?? null);
    setSelectedVideo(null);
    setSelectedPaper(null);
    setVideoQuery('');
    setTestQuery('');
    setTestObjectFilter('');
  }, [isOpen, initialData]);

  const fetchVideos = useCallback(async (q: string) => {
    setLoadingVideos(true);
    try {
      const res = await AdminApi.listVideos({ q: q || undefined, limit: 30 });
      if (res.success) setVideos(res.data.items);
    } catch (e) {
      console.error(e);
      toast.error('Không tải được danh sách video');
    } finally {
      setLoadingVideos(false);
    }
  }, []);

  const fetchPapers = useCallback(async (q: string, objectIdFilter: number | '') => {
    setLoadingPapers(true);
    try {
      const res = await AdminApi.listTestPapers({
        q: q || undefined,
        objectId: objectIdFilter === '' ? undefined : objectIdFilter,
        isActive: 1,
        limit: 40,
      });
      if (res.success) setPapers(res.data.items);
    } catch (e) {
      console.error(e);
      toast.error('Không tải được danh sách bài test');
    } finally {
      setLoadingPapers(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen || contentType !== 'VIDEO') return;
    const t = window.setTimeout(() => fetchVideos(videoQuery), 300);
    return () => clearTimeout(t);
  }, [isOpen, contentType, videoQuery, fetchVideos]);

  useEffect(() => {
    if (!isOpen || contentType !== 'TEST') return;
    AdminApi.listTestObjects()
      .then((res) => {
        if (res.success) setTestObjects(res.data);
      })
      .catch(console.error);
  }, [isOpen, contentType]);

  useEffect(() => {
    if (!isOpen || contentType !== 'TEST') return;
    const t = window.setTimeout(() => fetchPapers(testQuery, testObjectFilter), 300);
    return () => clearTimeout(t);
  }, [isOpen, contentType, testQuery, testObjectFilter, fetchPapers]);

  // Hydrate selected video/paper when editing
  useEffect(() => {
    if (!isOpen || !objectId) return;
    if (contentType === 'VIDEO' && videos.length) {
      const found = videos.find((v) => v.id === objectId);
      if (found) setSelectedVideo(found);
    }
    if (contentType === 'TEST' && papers.length) {
      const found = papers.find((p) => p.id === objectId);
      if (found) setSelectedPaper(found);
    }
  }, [isOpen, objectId, contentType, videos, papers]);

  const selectVideo = (v: AdminVideo) => {
    setSelectedVideo(v);
    setObjectId(v.id);
    applySourceTitle(v.title || '');
  };

  const selectPaper = (p: AdminTestPaper) => {
    setSelectedPaper(p);
    setObjectId(p.id);
    applySourceTitle(p.name || '');
  };

  const handleDetailChange = (html: string) => {
    setDetail(html);
    if (contentType !== 'POST') return;
    if (base64UploadTimerRef.current) {
      window.clearTimeout(base64UploadTimerRef.current);
    }
    if (!html.includes('data:image')) return;
    base64UploadTimerRef.current = window.setTimeout(async () => {
      try {
        setUploadingImage(true);
        const next = await replaceBase64ImagesInHtml(html);
        if (next !== html) setDetail(next);
      } finally {
        setUploadingImage(false);
      }
    }, 400);
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > ADMIN_VIDEO_MAX_BYTES) {
      toast.error('File vượt quá giới hạn 1.5GB');
      return;
    }
    try {
      setUploadingVideo(true);
      setVideoUploadProgress({
        phase: 'init',
        percent: 0,
        uploadedBytes: 0,
        totalBytes: file.size,
      });
      const res = await AdminApi.uploadVideoChunked(file, {
        title: name || undefined,
        onProgress: setVideoUploadProgress,
      });
      if (res.success && res.data) {
        selectVideo(res.data);
        setVideos((prev) => [res.data, ...prev.filter((v) => v.id !== res.data.id)]);
        toast.success('Đã upload video vào thư viện');
      } else {
        toast.error(res.message || 'Upload video thất bại');
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || 'Upload video thất bại');
    } finally {
      setUploadingVideo(false);
      setVideoUploadProgress(null);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Vui lòng nhập tên nội dung');
      return;
    }
    if (contentType === 'VIDEO' && !objectId) {
      toast.error('Vui lòng chọn hoặc upload video');
      return;
    }
    if (contentType === 'TEST' && !objectId) {
      toast.error('Vui lòng chọn bài test');
      return;
    }

    try {
      setIsSaving(true);
      let finalDetail = detail;
      if (contentType === 'POST' && detail.includes('data:image')) {
        finalDetail = await replaceBase64ImagesInHtml(detail);
      }

      const payload: Partial<AdminCourseContent> = {
        name: name.trim(),
        detail: contentType === 'FORDER' ? undefined : finalDetail,
        objectId: contentType === 'VIDEO' || contentType === 'TEST' ? objectId : null,
        parentId: parentId ?? null,
        contentType,
      };

      if (initialData?.id) {
        const res = await AdminApi.updateCourseContent(initialData.id, {
          name: payload.name,
          detail: payload.detail,
          objectId: payload.objectId,
        });
        if (res.success) {
          toast.success('Cập nhật thành công');
          onSaved();
          onClose();
        } else {
          toast.error(res.message || 'Có lỗi xảy ra');
        }
      } else {
        const res = await AdminApi.createCourseContent(courseId, payload);
        if (res.success) {
          toast.success('Thêm mới thành công');
          onSaved();
          onClose();
        } else {
          toast.error(res.message || 'Có lỗi xảy ra');
        }
      }
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Có lỗi xảy ra khi lưu');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-m2-surface-container rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">
            {initialData ? 'Chỉnh sửa' : 'Thêm'} {typeLabel(contentType).toLowerCase()}
          </h2>
          <button onClick={onClose} className="text-m2-on-surface-variant hover:text-m2-on-surface">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto">
          <div className="mb-4">
            <label className="block text-sm font-medium text-m2-on-surface mb-1">
              Tiêu đề {contentType === 'FORDER' ? 'thư mục' : 'bài học'}{' '}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="w-full border border-m2-outline rounded-md p-2 focus:ring-m2-primary focus:border-m2-primary"
              value={name}
              onChange={(e) => {
                setTitleManuallyEdited(true);
                setName(e.target.value);
              }}
              placeholder="Nhập tiêu đề..."
            />
            {(contentType === 'VIDEO' || contentType === 'TEST') && !titleManuallyEdited && (
              <p className="text-xs text-m2-on-surface-variant mt-1">
                Tiêu đề đang theo {contentType === 'VIDEO' ? 'video' : 'quiz'} đã chọn. Sửa ô này để giữ tiêu đề riêng.
              </p>
            )}
          </div>

          {contentType === 'POST' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-m2-on-surface mb-1">
                Nội dung bài viết
                {uploadingImage && (
                  <span className="ml-2 text-xs text-m2-primary">Đang upload ảnh...</span>
                )}
              </label>
              <div className="h-64 sm:h-96 pb-12 overflow-visible">
                <AdminRichTextEditor
                  value={detail}
                  onChange={handleDetailChange}
                  allowVideo
                  onUploadingChange={setUploadingImage}
                  className="h-full"
                />
              </div>
              <p className="text-xs text-m2-on-surface-variant mt-1">
                Có thể copy-paste ảnh/bảng vào editor. Icon bảng trên toolbar để chèn mới; click vào bảng rồi dùng +C/−C/+H/−H; kéo mép ô để giãn cột/hàng.
              </p>
            </div>
          )}

          {contentType === 'VIDEO' && (
            <div className="mb-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-m2-on-surface mb-1">
                  Chọn video từ thư viện <span className="text-red-500">*</span>
                </label>
                <div className="relative mb-2">
                  <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-m2-outline" />
                  <input
                    type="text"
                    className="w-full border border-m2-outline rounded-md pl-9 pr-3 py-2 text-sm"
                    placeholder="Tìm video theo tên..."
                    value={videoQuery}
                    onChange={(e) => setVideoQuery(e.target.value)}
                  />
                </div>
                <div className="border rounded-md max-h-48 overflow-y-auto">
                  {loadingVideos ? (
                    <p className="p-3 text-sm text-m2-on-surface-variant">Đang tải...</p>
                  ) : videos.length === 0 ? (
                    <p className="p-3 text-sm text-m2-on-surface-variant">Không có video nào</p>
                  ) : (
                    videos.map((v) => {
                      const selected = objectId === v.id;
                      return (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => selectVideo(v)}
                          className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 flex items-center justify-between hover:bg-m2-primary/10 ${
                            selected ? 'bg-m2-primary/10 text-m2-primary' : 'text-m2-on-surface'
                          }`}
                        >
                          <span className="truncate">{v.title}</span>
                          {selected && <CheckCircleIcon className="h-4 w-4 text-m2-primary shrink-0 ml-2" />}
                        </button>
                      );
                    })
                  )}
                </div>
                {(selectedVideo || objectId) && (
                  <p className="mt-2 text-xs text-green-700">
                    Đã chọn: {selectedVideo?.title || `Video #${objectId}`} (ID: {objectId})
                  </p>
                )}
              </div>

              <div className="border-t pt-4">
                <label className="block text-sm font-medium text-m2-on-surface mb-2">
                  Hoặc upload video mới
                </label>
                <label className="inline-flex items-center px-4 py-2 border border-dashed border-m2-outline rounded-md cursor-pointer hover:bg-m2-surface-low text-sm text-m2-on-surface">
                  <CloudArrowUpIcon className="h-5 w-5 mr-2 text-m2-on-surface-variant" />
                  {uploadingVideo
                    ? `Đang upload ${videoUploadProgress?.percent ?? 0}%...`
                    : 'Chọn file video'}
                  <input
                    type="file"
                    accept="video/*,.mp4,.webm,.mov,.mkv"
                    className="hidden"
                    disabled={uploadingVideo}
                    onChange={handleVideoUpload}
                  />
                </label>
                {videoUploadProgress && (
                  <div className="mt-2">
                    <div className="h-1.5 overflow-hidden rounded-full bg-m2-surface-high">
                      <div
                        className="h-full bg-m2-primary transition-all"
                        style={{ width: `${videoUploadProgress.percent}%` }}
                      />
                    </div>
                  </div>
                )}
                <p className="text-xs text-m2-on-surface-variant mt-1">
                  Tối đa 1.5GB (mp4/webm/mov/mkv). Upload chia chunk tự động. Video sẽ vào thư viện để dùng lại.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-m2-on-surface mb-1">Ghi chú (tuỳ chọn)</label>
                <div className="min-h-[160px]">
                  <AdminRichTextEditor value={detail} onChange={setDetail} />
                </div>
              </div>
            </div>
          )}

          {contentType === 'TEST' && (
            <div className="mb-4 space-y-3">
              <label className="block text-sm font-medium text-m2-on-surface">
                Chọn bài test (đang hoạt động) <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <select
                  className="border border-m2-outline rounded-md px-3 py-2 text-sm sm:w-48"
                  value={testObjectFilter}
                  onChange={(e) =>
                    setTestObjectFilter(e.target.value === '' ? '' : Number(e.target.value))
                  }
                >
                  <option value="">Tất cả đối tượng</option>
                  {testObjects.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
                <div className="relative flex-1">
                  <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-m2-outline" />
                  <input
                    type="text"
                    className="w-full border border-m2-outline rounded-md pl-9 pr-3 py-2 text-sm"
                    placeholder="Tìm bài test theo tên..."
                    value={testQuery}
                    onChange={(e) => setTestQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="border rounded-md max-h-56 overflow-y-auto">
                {loadingPapers ? (
                  <p className="p-3 text-sm text-m2-on-surface-variant">Đang tải...</p>
                ) : papers.length === 0 ? (
                  <p className="p-3 text-sm text-m2-on-surface-variant">Không có bài test phù hợp</p>
                ) : (
                  papers.map((p) => {
                    const selected = objectId === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => selectPaper(p)}
                        className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 hover:bg-green-50 ${
                          selected ? 'bg-green-50 text-green-900' : 'text-m2-on-surface'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium truncate">{p.name}</span>
                          {selected && <CheckCircleIcon className="h-4 w-4 text-m2-secondary shrink-0 ml-2" />}
                        </div>
                        <div className="text-xs text-m2-on-surface-variant mt-0.5">
                          ID: {p.id}
                          {p.totalQuestion != null ? ` · ${p.totalQuestion} câu` : ''}
                          {p.minute != null ? ` · ${p.minute} phút` : ''}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
              {selectedPaper && (
                <p className="text-xs text-green-700">Đã chọn: {selectedPaper.name}</p>
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t flex justify-end space-x-3 bg-m2-surface-low rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-m2-outline rounded-md text-m2-on-surface bg-m2-surface-container hover:bg-m2-surface-low"
            disabled={isSaving}
          >
            Hủy
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || uploadingImage || uploadingVideo}
            className="px-4 py-2 bg-m2-primary text-m2-on-primary rounded-md hover:bg-eh-forest-shadow disabled:opacity-50"
          >
            {isSaving ? 'Đang lưu...' : 'Lưu'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CourseContentEditorModal;
